import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faFileAudio, faTimes, faTrash, faEdit, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Form, Button, Alert, InputGroup, Modal, ProgressBar, Table, Badge } from '@themesberg/react-bootstrap';
import { uploadToR2, generateUUID } from '../../utils/r2Upload';
import api from '../../utils/api';

const FILTER_LABELS = {
  everyone: 'Herkese gönder',
  vip: 'Şu anda VIP olanlar',
  active7: 'Son 7 günde aktif',
  active30: 'Son 30 günde aktif',
  active90: 'Son 90 günde aktif',
  neverSpokenActive30: 'Hiç konuşmadıkları + 30 gün aktif',
  neverSpoken: 'Daha önce konuşmadıkları',
  previouslySpoken: 'Daha önce konuştukları',
  purchased: 'Satın alım geçmişi olanlar',
  lastMonth: 'Son 1 aylık üyeler',
  favorites: 'Favorilerdeki kullanıcılar'
};

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default () => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [filteredFakeAccounts, setFilteredFakeAccounts] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState("");
  const [showAccountModal, setShowAccountModal] = useState(false);

  const [filterType, setFilterType] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [mediaType, setMediaType] = useState("text");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [editJob, setEditJob] = useState(null);

  // Hazır şablon seçimi
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateMediaUrl, setTemplateMediaUrl] = useState(null);

  // Job tracking state
  const [activeJob, setActiveJob] = useState(null);
  const jobUnsubscribeRef = useRef(null);

  const openTemplateModal = async () => {
    setShowTemplateModal(true);
    setTemplatesLoading(true);
    try {
      const res = await api.get("/auto-msg/templates");
      setTemplates(res.data?.templates || []);
    } catch (err) {
      setError("Şablonlar yüklenemedi: " + (err.response?.data?.error || err.message));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const applyTemplate = (t) => {
    setMessageText(t.text || "");
    const type = t.mediaType || "text";
    setMediaType(type);
    setSelectedImage(null);
    setSelectedAudio(null);
    setSelectedVideo(null);
    setImagePreview(type === "image" && t.mediaUrl ? t.mediaUrl : null);
    setTemplateMediaUrl(t.mediaUrl || null);
    setShowTemplateModal(false);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchFakeAccounts = async () => {
      try {
        const response = await api.get('/users?isTestUser=true&limit=500');
        if (isMounted) {
          const accounts = [];
          // Backend { success, users, total } zarfı döndürüyor
          const usersData = response.data?.users || response.data?.data || response.data || [];
          (Array.isArray(usersData) ? usersData : []).forEach(user => {
            accounts.push({
              id: user.uid || user._id || user.id,
              name: user.name || user.displayName || user.firstName || '',
              profileImageUrl: user.profileImageUrl || null
            });
          });

          accounts.sort((a, b) => a.name.localeCompare(b.name));
          setFakeAccounts(accounts);
          setFilteredFakeAccounts(accounts);
        }
      } catch (err) {
        console.error("Error loading fake accounts:", err);
        if (isMounted) setError("Fake hesaplar yüklenirken hata oluştu: " + err.message);
      }
    };
    fetchFakeAccounts();
    loadScheduledJobs();

    return () => {
      isMounted = false;
      if (jobUnsubscribeRef.current) {
        clearInterval(jobUnsubscribeRef.current);
      }
    };
  }, []);

  const loadScheduledJobs = async () => {
    try {
      const res = await api.get('/bulk/scheduled');
      setScheduledJobs(res.data?.jobs || []);
    } catch (err) {
      console.error('Zamanlanmış toplu mesajlar yüklenemedi:', err);
    }
  };

  // Hesap arama
  useEffect(() => {
    if (!accountSearchTerm.trim()) {
      setFilteredFakeAccounts(fakeAccounts);
      return;
    }

    const searchLower = accountSearchTerm.toLowerCase().trim();
    const filtered = fakeAccounts.filter(account =>
      account.name.toLowerCase().includes(searchLower)
    ).sort((a, b) => a.name.localeCompare(b.name));

    setFilteredFakeAccounts(filtered);
  }, [accountSearchTerm, fakeAccounts]);

  // Kişi sayısını hesapla (server-side Cloud Function ile)
  const calculateRecipientCount = async () => {
    if (!selectedAccount) {
      setError("Lütfen önce bir hesap seçin.");
      return;
    }

    setCalculating(true);
    setError(null);

    try {
      const result = await api.post('/bulk/calculate-recipients', {
        senderAccountId: selectedAccount.id,
        filterType: filterType
      });

      if (result.data && (result.data.success || result.data.status === 'success')) {
        setRecipientCount(result.data.count || result.data.data?.count);
      } else {
        throw new Error("Kişi sayısı hesaplanırken sunucu hatası oluştu.");
      }
    } catch (err) {
      console.error("Error calculating recipient count:", err);
      setError("Kişi sayısı hesaplanırken hata oluştu: " + err.message);
    } finally {
      setCalculating(false);
    }
  };

  // Medya seçimi
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setTemplateMediaUrl(null);
      setMediaType("image");
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedAudio(file);
      setTemplateMediaUrl(null);
      setMediaType("audio");
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedVideo(file);
      setTemplateMediaUrl(null);
      setMediaType("video");
    }
  };

  // Mesaj gönder (Job oluştur)
  const handleSendMessages = async () => {
    if (!selectedAccount) {
      setError("Lütfen bir hesap seçin.");
      return;
    }

    if (!filterType) {
      setError("Lütfen bir filtre seçin.");
      return;
    }

    if (scheduleEnabled && !scheduledAt) {
      setError("Zamanlamak için tarih ve saat seçin.");
      return;
    }

    if (mediaType === "text" && !messageText.trim()) {
      setError("Lütfen bir mesaj yazın.");
      return;
    }

    if (mediaType === "image" && !selectedImage && !templateMediaUrl && !messageText.trim()) {
      setError("Lütfen bir resim seçin veya mesaj yazın.");
      return;
    }

    if (mediaType === "audio" && !selectedAudio && !templateMediaUrl && !messageText.trim()) {
      setError("Lütfen bir ses dosyası seçin veya mesaj yazın.");
      return;
    }

    if (mediaType === "video" && !selectedVideo && !templateMediaUrl && !messageText.trim()) {
      setError("Lütfen bir video dosyası seçin veya mesaj yazın.");
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      // Medya yükle (varsa) — şablondan gelen URL varsa onu kullan
      let mediaUrl = templateMediaUrl || null;
      if (mediaType === "image" && selectedImage) {
        const r2Path = `bulk_messages/images/${generateUUID()}.jpg`;
        mediaUrl = await uploadToR2(selectedImage, r2Path);
      } else if (mediaType === "audio" && selectedAudio) {
        const r2Path = `bulk_messages/audio/${generateUUID()}.m4a`;
        mediaUrl = await uploadToR2(selectedAudio, r2Path);
      } else if (mediaType === "video" && selectedVideo) {
        const r2Path = `bulk_messages/video/${generateUUID()}.mp4`;
        mediaUrl = await uploadToR2(selectedVideo, r2Path);
      }

      const jobData = {
        senderAccountId: selectedAccount.id,
        senderName: selectedAccount.name,
        filterType: filterType,
        messageText: messageText || "",
        mediaType: mediaType,
        mediaUrl: mediaUrl,
        scheduledAt: scheduleEnabled ? new Date(scheduledAt).toISOString() : null
      };

      const result = await api.post('/bulk/send', jobData);

      if (scheduleEnabled) {
        setSending(false);
        setSuccess(true);
        setScheduleEnabled(false);
        setScheduledAt('');
        await loadScheduledJobs();
        alert('Toplu mesaj zamanlandı. Kişi listesi gönderim anında yeniden hesaplanacak.');
        return;
      }
      const jobId = result.data.jobId || result.data.data?.jobId || generateUUID();

      console.log("Bulk message job created:", jobId);

      setActiveJob({
        id: jobId,
        status: "pending",
        progress: 0,
        successCount: 0,
        failCount: 0,
        totalRecipients: recipientCount,
        ...jobData
      });

      if (jobUnsubscribeRef.current) {
        clearInterval(jobUnsubscribeRef.current);
      }

      // API kullanarak status polling yap
      const pollJobStatus = async () => {
        try {
          // Bu endpointin var olduğunu varsayıyoruz
          const statusRes = await api.get(`/bulk/status/${jobId}`);
          const data = statusRes.data.data || statusRes.data;
          
          setActiveJob(prev => ({
            ...prev,
            ...data,
            id: jobId
          }));

          if (data.status === "completed") {
            setSending(false);
            setSuccess(true);
            setMessageText("");
            setSelectedImage(null);
            setSelectedAudio(null);
            setSelectedVideo(null);
            setImagePreview(null);
            setTemplateMediaUrl(null);
            setMediaType("text");
            setRecipientCount(null);
            setFilterType(null);

            alert(`İşlem tamamlandı! 🎉\n\nBaşarılı: ${data.successCount}\nBaşarısız: ${data.failCount}\n\nTüm mesajlar arka planda gönderildi.`);

            if (jobUnsubscribeRef.current) {
              clearInterval(jobUnsubscribeRef.current);
              jobUnsubscribeRef.current = null;
            }
            setTimeout(() => setActiveJob(null), 3000);
          } else if (data.status === "failed") {
            setSending(false);
            setError("Toplu mesaj gönderimi başarısız oldu: " + (data.error || "Bilinmeyen hata"));

            if (jobUnsubscribeRef.current) {
              clearInterval(jobUnsubscribeRef.current);
              jobUnsubscribeRef.current = null;
            }
            setTimeout(() => setActiveJob(null), 5000);
          }
        } catch (e) {
          console.error("Job status check error:", e);
        }
      };

      jobUnsubscribeRef.current = setInterval(pollJobStatus, 2000);
      pollJobStatus();

    } catch (err) {
      console.error("Error creating bulk message job:", err);
      setError("Toplu mesaj isteği oluşturulurken hata oluştu: " + (err.response?.data?.error || err.message || err.toString()));
      setSending(false);
    }
  };

  return (
    <>
      <div className="py-4">
        <h4>Toplu Mesaj Gönder</h4>
        <p className="text-muted mb-0">Mesajlar / Toplu Mesaj Gönder</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
          Mesajlar başarıyla gönderildi!
        </Alert>
      )}

      {/* Active Job Progress */}
      {activeJob && activeJob.status !== "completed" && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <h5 className="mb-3">
              {activeJob.status === "pending" ? "⏳ İş kuyruğa alındı..." :
                activeJob.status === "running" ? "🚀 Mesajlar gönderiliyor..." :
                  activeJob.status === "failed" ? "❌ Hata oluştu" : ""}
            </h5>

            {activeJob.status === "running" && (
              <>
                <ProgressBar
                  now={activeJob.progress || 0}
                  label={`${activeJob.progress || 0}%`}
                  animated
                  striped
                  variant="success"
                  className="mb-3"
                  style={{ height: '25px' }}
                />
                <div className="d-flex justify-content-between text-muted">
                  <span>Gönderilen: <strong>{activeJob.successCount || 0}</strong></span>
                  <span>Toplam: <strong>{activeJob.totalRecipients || "hesaplanıyor..."}</strong></span>
                  <span>Başarısız: <strong>{activeJob.failCount || 0}</strong></span>
                </div>
              </>
            )}

            {activeJob.status === "pending" && (
              <div className="text-muted">
                İş kuyruğa alındı... Birkaç saniye içinde başlayacak.
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="alert alert-info mb-4">
            <strong>Bilgi:</strong> Mesajlar arka planda gönderilir. Sayfa açık kaldığı sürece ilerlemeyi takip edebilirsiniz.
          </div>

          {/* Hesap Seçin */}
          <div className="mb-4">
            <Form.Label className="mb-2">
              <strong>Hesap Seçin</strong>
            </Form.Label>
            <div>
              {selectedAccount ? (
                <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                  {selectedAccount.profileImageUrl ? (
                    <img
                      src={selectedAccount.profileImageUrl}
                      alt={selectedAccount.name}
                      className="rounded-circle"
                      style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: '0.8rem', color: '#fff' }}>
                        {selectedAccount.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="flex-grow-1">{selectedAccount.name}</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setSelectedAccount(null);
                      setFilterType(null);
                      setRecipientCount(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>
              ) : (
                <Button variant="outline-primary" onClick={() => setShowAccountModal(true)}>
                  Hesap Seçin
                </Button>
              )}
            </div>
          </div>

          {/* Filtre Butonları */}
          {selectedAccount && (
            <div className="mb-4">
              <Form.Label className="mb-2">
                <strong>Gönderilecek Kişileri Seçin</strong>
              </Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  variant={filterType === "everyone" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("everyone"); setRecipientCount(null); }}
                >
                  Herkese Gönder
                </Button>
                <Button
                  variant={filterType === "vip" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("vip"); setRecipientCount(null); }}
                >
                  Şu anda VIP olanlar
                </Button>
                <Button
                  variant={filterType === "active7" ? "success" : "outline-info"}
                  size="sm"
                  onClick={() => { setFilterType("active7"); setRecipientCount(null); }}
                >
                  Son 7 günde aktif
                </Button>
                <Button
                  variant={filterType === "active30" ? "success" : "outline-info"}
                  size="sm"
                  onClick={() => { setFilterType("active30"); setRecipientCount(null); }}
                >
                  Son 30 günde aktif
                </Button>
                <Button
                  variant={filterType === "active90" ? "success" : "outline-info"}
                  size="sm"
                  onClick={() => { setFilterType("active90"); setRecipientCount(null); }}
                >
                  Son 90 günde aktif
                </Button>
                <Button
                  variant={filterType === "neverSpokenActive30" ? "success" : "outline-info"}
                  size="sm"
                  onClick={() => { setFilterType("neverSpokenActive30"); setRecipientCount(null); }}
                >
                  Hiç konuşmadıkları + 30 gün aktif
                </Button>
                <Button
                  variant={filterType === "neverSpoken" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("neverSpoken"); setRecipientCount(null); }}
                >
                  Daha önce konuşmadıkları
                </Button>
                <Button
                  variant={filterType === "previouslySpoken" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("previouslySpoken"); setRecipientCount(null); }}
                >
                  Daha önce konuştukları
                </Button>
                <Button
                  variant={filterType === "purchased" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("purchased"); setRecipientCount(null); }}
                >
                  Satın alım geçmişi olanlar
                </Button>
                <Button
                  variant={filterType === "lastMonth" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("lastMonth"); setRecipientCount(null); }}
                >
                  Son 1 aylık üyeler
                </Button>
                <Button
                  variant={filterType === "favorites" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => { setFilterType("favorites"); setRecipientCount(null); }}
                >
                  Favorilerdeki kullanıcılar
                </Button>
              </div>

              {filterType && (
                <div className="mb-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={calculateRecipientCount}
                    disabled={calculating}
                  >
                    {calculating ? "Hesaplanıyor..." : "Kişi sayısını gör"}
                  </Button>
                  {recipientCount !== null && (
                    <div className="mt-2">
                      <strong>Mesaj gönderilecek kişi sayısı: {recipientCount}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mesaj Yazma */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">
                <strong>Gönderilecek Mesaj</strong>
              </Form.Label>
              <Button variant="outline-success" size="sm" onClick={openTemplateModal}>
                Hazır Mesajlardan Seç
              </Button>
            </div>

            {/* Medya Tipi Seçimi */}
            <div className="d-flex gap-2 mb-3">
              <Button
                variant={mediaType === "text" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => {
                  setMediaType("text");
                  setSelectedImage(null);
                  setSelectedAudio(null);
                  setSelectedVideo(null);
                  setImagePreview(null);
                  setTemplateMediaUrl(null);
                }}
              >
                Metin
              </Button>
              <Button
                variant={mediaType === "image" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setMediaType("image")}
              >
                <FontAwesomeIcon icon={faImage} className="me-1" />
                Resim
              </Button>
              <Button
                variant={mediaType === "audio" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setMediaType("audio")}
              >
                <FontAwesomeIcon icon={faFileAudio} className="me-1" />
                Ses
              </Button>
              <Button
                variant={mediaType === "video" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setMediaType("video")}
              >
                <FontAwesomeIcon icon={faVideo} className="me-1" />
                Video
              </Button>
            </div>

            {/* Resim Seçimi ve Önizleme */}
            {mediaType === "image" && (
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                />
                {imagePreview && (
                  <div className="mt-3">
                    <div className="position-relative d-inline-block">
                      <img
                        src={imagePreview}
                        alt="Önizleme"
                        style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px' }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        onClick={() => {
                          setSelectedImage(null);
                          setImagePreview(null);
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                    <div className="mt-2">
                      <strong>Yüklenen resim</strong>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Ses Seçimi */}
            {mediaType === "audio" && (
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioSelect}
                />
                {selectedAudio && (
                  <div className="mt-2">
                    <strong>Seçilen dosya:</strong> {selectedAudio.name}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setSelectedAudio(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Video Seçimi */}
            {mediaType === "video" && (
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="video/*"
                  onChange={handleVideoSelect}
                />
                {selectedVideo && (
                  <div className="mt-2">
                    <strong>Seçilen dosya:</strong> {selectedVideo.name}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setSelectedVideo(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Metin Mesajı */}
            <div className="mb-2">
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => setMessageText(prev => prev + " {{uye_sehir_da}} {{first_uye_name}}")}
                title="Kullanıcının adı ve şehrine göre akıllı etiket ekler"
              >
                Akıllı Mesaj (Ad - Şehir) Ekle
              </Button>
              <small className="ms-2 text-muted">Örn: İstanbul'da yaşıyorsun, Merhaba Alican Bey</small>
            </div>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder={mediaType === "text" ? "Mesaj yazın..." : "Mesaj yazın (opsiyonel)..."}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />
          </div>

          {/* Gönder Butonu */}
          <div className="mb-4">
            <div className="form-check mb-3">
              <input
                id="schedule-bulk"
                type="checkbox"
                className="form-check-input"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.currentTarget.checked)}
              />
              <label className="form-check-label" htmlFor="schedule-bulk">
                Toplu mesajı zamanla (tek seferlik)
              </label>
            </div>
            {scheduleEnabled && (
              <div className="mb-3">
                <Form.Label>Gönderileceği tarih ve saat</Form.Label>
                <Form.Control
                  type="datetime-local"
                  style={{ maxWidth: 280 }}
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.currentTarget.value)}
                />
                <small className="text-muted d-block mt-2">
                  Filtre (ör. konuşmadıkları) mesaj zamanlandığında değil, gönderileceği anda çalışır.
                </small>
              </div>
            )}
            <Button
              variant="primary"
              size="lg"
              onClick={handleSendMessages}
              disabled={sending || !selectedAccount || !filterType || (!scheduleEnabled && recipientCount === null)}
            >
              {sending ? "İşlem devam ediyor..." : (scheduleEnabled ? "Zamanla" : "Mesajları Gönder")}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3">Zamanlanmış Toplu Mesajlar</h5>
          {scheduledJobs.length === 0 ? (
            <p className="text-muted mb-0">Zamanlanmış toplu mesaj yok.</p>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Hesap</th>
                  <th>Filtre</th>
                  <th>Mesaj</th>
                  <th>Tarih</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {scheduledJobs.map((job) => (
                  <tr key={job.id}>
                    <td>{job.senderName || job.senderAccountId}</td>
                    <td>{FILTER_LABELS[job.filterType] || job.filterType}</td>
                    <td><small>{job.messageText || (job.mediaType !== 'text' ? job.mediaType : '-')}</small></td>
                    <td>{formatDate(job.scheduledAt)}</td>
                    <td>
                      <Badge bg={job.status === 'paused' ? 'warning' : 'primary'}>
                        {job.status === 'paused' ? 'Pasif' : 'Aktif'}
                      </Badge>
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setEditJob({
                        ...job,
                        scheduledAtLocal: toDatetimeLocal(job.scheduledAt)
                      })}>
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        className="me-1"
                        onClick={async () => {
                          await api.patch(`/bulk/${job.id}`, {
                            status: job.status === 'paused' ? 'scheduled' : 'paused'
                          });
                          await loadScheduledJobs();
                        }}
                      >
                        <FontAwesomeIcon icon={job.status === 'paused' ? faPlay : faPause} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={async () => {
                          if (!window.confirm('Bu zamanlanmış mesaj silinsin mi?')) return;
                          await api.delete(`/bulk/${job.id}`);
                          await loadScheduledJobs();
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Hazır Şablon Modal */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Hazır Mesajlardan Seç</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {templatesLoading ? (
            <div className="text-center py-4 text-muted">Yükleniyor...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-4 text-muted">
              Henüz şablon yok. Otomatik Mesaj → Hazır Şablonlar menüsünden ekleyebilirsiniz.
            </div>
          ) : (
            <div className="list-group">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="list-group-item list-group-item-action"
                  onClick={() => applyTemplate(t)}
                >
                  <div className="d-flex justify-content-between">
                    <strong>{t.title}</strong>
                    <span className="badge bg-secondary">{t.mediaType}</span>
                  </div>
                  <div className="text-muted small text-truncate">{t.text || "—"}</div>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Hesap Seçim Modal */}
      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Hesap Seçin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="İsim ile ara..."
              value={accountSearchTerm}
              onChange={(e) => setAccountSearchTerm(e.target.value)}
            />
          </InputGroup>
          <div className="d-flex flex-wrap gap-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredFakeAccounts.map((account) => (
              <div
                key={account.id}
                className="d-flex flex-column align-items-center p-2 border rounded"
                style={{ cursor: 'pointer', width: '120px' }}
                onClick={() => {
                  setSelectedAccount(account);
                  setShowAccountModal(false);
                  setAccountSearchTerm("");
                }}
              >
                {account.profileImageUrl ? (
                  <img
                    src={account.profileImageUrl}
                    alt={account.name}
                    className="rounded-circle mb-2"
                    style={{ width: 49, height: 49, objectFit: 'cover' }}
                  />
                ) : (
                  <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mb-2" style={{ width: 49, height: 49 }}>
                    <span style={{ fontSize: '0.9rem', color: '#fff' }}>
                      {account.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-center small">{account.name}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={!!editJob} onHide={() => setEditJob(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Zamanlanmış Mesajı Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editJob && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Filtre</Form.Label>
                <Form.Select
                  value={editJob.filterType}
                  onChange={(e) => {
                    const value = e && e.target ? e.target.value : '';
                    setEditJob((prev) => ({ ...prev, filterType: value }));
                  }}
                >
                  {Object.entries(FILTER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Mesaj</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editJob.messageText || ''}
                  onChange={(e) => {
                    const value = e && e.target ? e.target.value : '';
                    setEditJob((prev) => ({ ...prev, messageText: value }));
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Tarih ve saat</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={editJob.scheduledAtLocal || ''}
                  onChange={(e) => {
                    const value = e && e.target ? e.target.value : '';
                    setEditJob((prev) => ({ ...prev, scheduledAtLocal: value }));
                  }}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditJob(null)}>Vazgeç</Button>
          <Button
            variant="primary"
            onClick={async () => {
              await api.patch(`/bulk/${editJob.id}`, {
                filterType: editJob.filterType,
                messageText: editJob.messageText,
                scheduledAt: editJob.scheduledAtLocal ? new Date(editJob.scheduledAtLocal).toISOString() : undefined
              });
              setEditJob(null);
              await loadScheduledJobs();
            }}
          >
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
